import { useState, useEffect } from 'react';
import { homepageCategoryAPI, categoryAPI, IMAGE_BASE } from '../../services/api';
import { Save, Upload, Image as ImageIcon } from 'lucide-react';

export default function HomepageCategoriesAdmin() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [hcRes, catRes] = await Promise.all([
                homepageCategoryAPI.getAll(),
                categoryAPI.getAll({ active: 'true' })
            ]);
            setItems(hcRes.data || []);
            setCategories(catRes.data || []);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    const handleSave = async (item, index) => {
        setSaving(item.id);
        setMessage('');
        try {
            const formData = new FormData();
            formData.append('title', item.title);
            formData.append('categoryId', item.categoryId || '');
            if (item._newImage) {
                formData.append('image', item._newImage);
            }
            const res = await homepageCategoryAPI.update(item.id, formData);
            const updated = [...items];
            updated[index] = { ...res.data, _newImage: null, _preview: null };
            setItems(updated);
            setMessage(`Kart ${item.position} kaydedildi.`);
        } catch (e) {
            console.error(e);
            setMessage('Hata: Kaydedilemedi.');
        }
        setSaving(null);
    };

    const handleChange = (index, field, value) => {
        const updated = [...items];
        updated[index] = { ...updated[index], [field]: value };
        setItems(updated);
    };

    const handleImageChange = (index, file) => {
        if (!file) return;
        const updated = [...items];
        updated[index] = {
            ...updated[index],
            _newImage: file,
            _preview: URL.createObjectURL(file)
        };
        setItems(updated);
    };

    // Build category options with hierarchy
    const parentCategories = categories.filter(c => !c.parentId);
    const getCategoryOptions = () => {
        const options = [];
        parentCategories.forEach(parent => {
            options.push({ id: parent.id, name: parent.name, isParent: true });
            const subs = categories.filter(c => c.parentId === parent.id);
            subs.forEach(sub => {
                options.push({ id: sub.id, name: `  └ ${sub.name}`, isParent: false });
            });
        });
        return options;
    };

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <h1>Anasayfa Kategori Kartları</h1>
                <p style={{ color: '#888', fontSize: '0.9rem', marginTop: '4px' }}>
                    Anasayfada görünen 4 kategori kartını buradan yönetebilirsiniz. Kart sayısı sabittir, yalnızca içerikleri düzenlenebilir.
                </p>
            </div>

            {message && (
                <div className={`admin-alert ${message.startsWith('Hata') ? 'admin-alert-error' : 'admin-alert-success'}`}>
                    {message}
                </div>
            )}

            <div className="homepage-cat-grid">
                {items.map((item, index) => {
                    const imgSrc = item._preview || (item.image ? (item.image.startsWith('http') ? item.image : IMAGE_BASE + item.image) : null);
                    return (
                        <div key={item.id} className="homepage-cat-card">
                            <div className="homepage-cat-card-header">
                                <span className="homepage-cat-position">{item.position}</span>
                                <span className="homepage-cat-position-label">Kart {item.position}</span>
                            </div>

                            <div className="homepage-cat-image-area">
                                {imgSrc ? (
                                    <img src={imgSrc} alt={item.title} className="homepage-cat-preview" />
                                ) : (
                                    <div className="homepage-cat-no-image">
                                        <ImageIcon size={40} />
                                        <span>Görsel Yok</span>
                                    </div>
                                )}
                                <label className="homepage-cat-upload-btn">
                                    <Upload size={14} /> Görsel Seç
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={(e) => handleImageChange(index, e.target.files[0])}
                                    />
                                </label>
                            </div>

                            <div className="homepage-cat-fields">
                                <div className="form-group">
                                    <label>Başlık</label>
                                    <input
                                        type="text"
                                        value={item.title || ''}
                                        onChange={(e) => handleChange(index, 'title', e.target.value)}
                                        placeholder="Kategori başlığı"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Bağlı Kategori</label>
                                    <select
                                        value={item.categoryId || ''}
                                        onChange={(e) => handleChange(index, 'categoryId', e.target.value ? parseInt(e.target.value) : null)}
                                    >
                                        <option value="">-- Kategori Seç --</option>
                                        {getCategoryOptions().map(opt => (
                                            <option key={opt.id} value={opt.id}>
                                                {opt.name}
                                            </option>
                                        ))}
                                    </select>
                                    {item.category && (
                                        <span className="homepage-cat-slug-info">
                                            Slug: {item.category.slug}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <button
                                className="btn btn-primary homepage-cat-save-btn"
                                onClick={() => handleSave(item, index)}
                                disabled={saving === item.id}
                            >
                                <Save size={14} /> {saving === item.id ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
